import { useEffect, useMemo, useRef, useState } from 'react'
import {
  collection,
  doc,
  onSnapshot,
  query,
  where,
  documentId,
  Timestamp,
} from 'firebase/firestore'
import type { DocumentData, Query, QueryConstraint } from 'firebase/firestore'
import { db } from './firebase'
import type { WithId } from '../types/models'

/* ============================================================================
   Live reads.

   Everything in Encore is a snapshot listener rather than a fetch, for the
   same reason the native apps are: a tour is edited by several people at once
   and a stale running order is the one thing the app must never show.
   ============================================================================ */

export interface QueryState<T> {
  data: WithId<T>[]
  loading: boolean
  error: Error | null
}

/**
 * Subscribes to a collection.
 *
 * Pass `null` for `constraints` to stand down entirely — used when a required
 * id isn't known yet, which is cleaner than subscribing to a query that
 * matches nothing.
 *
 * Security rules filter nothing: a query that *could* return a document the
 * user cannot read is rejected outright rather than trimmed. Every query
 * against a tour-scoped collection must therefore constrain `tourId`.
 */

/** An ISO-8601 instant, the only string shape that becomes a Timestamp. */
const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/

/**
 * Repairs a document on the way in.
 *
 * Every screen calls `.toDate()` on a date field — two hundred call sites —
 * so a document whose date is a *string* doesn't render wrongly, it throws,
 * and React unmounts the page to white. That can happen from a bad import, a
 * half-finished migration, or a client that wrote the wrong shape.
 *
 * It is worse with Firestore's local cache in play: the first render comes
 * from cache, so a crash there happens before the server's corrected copy
 * arrives, and the page dies in a loop against data that is already fixed.
 *
 * Coercing here means one place has to be right instead of two hundred, and
 * anything already a Timestamp passes through untouched.
 */
function repair<T>(value: T): T {
  if (Array.isArray(value)) return value.map(repair) as unknown as T
  if (!value || typeof value !== 'object') return value

  // Timestamps, GeoPoints and DocumentReferences are class instances — leave
  // them exactly as they are.
  if (value.constructor !== Object) return value

  // A Timestamp that lost its class on the way through something — an import
  // that walked it field by field, a JSON round trip — arrives as a plain map
  // of seconds and nanoseconds. It reads as an object, so nothing downstream
  // notices until `.toMillis()` is called on it and the page dies.
  const asRecord = value as Record<string, unknown>
  const seconds = asRecord._seconds ?? asRecord.seconds
  const nanos = asRecord._nanoseconds ?? asRecord.nanoseconds
  if (typeof seconds === 'number' && typeof nanos === 'number') {
    return new Timestamp(seconds, nanos) as unknown as T
  }

  const out: Record<string, unknown> = {}
  for (const [key, entry] of Object.entries(asRecord)) {
    if (typeof entry === 'string' && ISO_INSTANT.test(entry)) {
      const parsed = new Date(entry)
      out[key] = isNaN(parsed.getTime()) ? entry : Timestamp.fromDate(parsed)
    } else {
      out[key] = repair(entry)
    }
  }
  return out as T
}

export function useCollection<T>(
  path: string,
  constraints: QueryConstraint[] | null,
): QueryState<T> {
  const [state, setState] = useState<QueryState<T>>({ data: [], loading: true, error: null })

  // Constraint arrays are rebuilt every render. Without a stable key the
  // effect tears down and re-subscribes on each one, which flickers the list
  // and re-bills the read.
  const key = useMemo(
    () => (constraints === null ? null : JSON.stringify(constraints.map(describeConstraint))),
    [constraints],
  )
  const latestConstraints = useRef(constraints)
  latestConstraints.current = constraints

  useEffect(() => {
    if (key === null) {
      setState({ data: [], loading: false, error: null })
      return
    }

    setState((previous) => ({ ...previous, loading: true }))

    const built = query(
      collection(db, path),
      ...(latestConstraints.current ?? []),
    ) as Query<DocumentData>

    return onSnapshot(
      built,
      (snapshot) => {
        setState({
          data: snapshot.docs.map((entry) => repair({ id: entry.id, ...entry.data() }) as WithId<T>),
          loading: false,
          error: null,
        })
      },
      (error) => {
        console.error(`[firestore] ${path}`, error)
        setState({ data: [], loading: false, error })
      },
    )
  }, [path, key])

  return state
}

/** Subscribes to a single document. `null` id stands the listener down. */
export function useDocument<T>(path: string, id: string | null | undefined) {
  const [state, setState] = useState<{
    data: WithId<T> | null
    loading: boolean
    error: Error | null
  }>({ data: null, loading: true, error: null })

  useEffect(() => {
    if (!id) {
      setState({ data: null, loading: false, error: null })
      return
    }

    return onSnapshot(
      doc(db, path, id),
      (snapshot) => {
        setState({
          data: snapshot.exists() ? (repair({ id: snapshot.id, ...snapshot.data() }) as WithId<T>) : null,
          loading: false,
          error: null,
        })
      },
      (error) => {
        console.error(`[firestore] ${path}/${id}`, error)
        setState({ data: null, loading: false, error })
      },
    )
  }, [path, id])

  return state
}

/**
 * A stable description of a constraint, for the memo key.
 *
 * Firestore's constraint objects carry no public identity, so this reaches for
 * the private fields and falls back to the type alone. Worst case a query
 * re-subscribes more often than it needs to — never less.
 */
function describeConstraint(constraint: QueryConstraint): unknown {
  const internal = constraint as unknown as {
    type: string
    _field?: unknown
    _op?: unknown
    _value?: unknown
    _direction?: unknown
    _limit?: unknown
  }
  return [
    internal.type,
    String(internal._field ?? ''),
    String(internal._op ?? ''),
    JSON.stringify(internal._value ?? null),
    String(internal._direction ?? ''),
    String(internal._limit ?? ''),
  ]
}

/* ============================================================================
   Helpers
   ============================================================================ */

/** Firestore rejects `in` with an empty array, and chunks above 30. */
export function chunkIds(ids: string[], size = 30): string[][] {
  const chunks: string[][] = []
  for (let index = 0; index < ids.length; index += size) {
    chunks.push(ids.slice(index, index + size))
  }
  return chunks
}

export function whereDocumentIdIn(ids: string[]): QueryConstraint {
  return where(documentId(), 'in', ids)
}

/** Firestore `Timestamp` → JS `Date`, tolerating a missing value. */
export function toDate(value: Timestamp | null | undefined): Date | null {
  return value ? value.toDate() : null
}

/**
 * Strips `id` before a write.
 *
 * `id` comes from the snapshot, not the document. Writing it back creates a
 * real field that shadows the document ID and quietly breaks every `@DocumentID`
 * decode in the Swift apps.
 */
export function withoutId<T extends { id?: string }>(value: T): Omit<T, 'id'> {
  const { id: _id, ...rest } = value
  return rest
}

/**
 * Drops `undefined` values.
 *
 * Firestore rejects `undefined` outright, and an optional field that simply
 * isn't set must be absent rather than null — the Swift decoders treat an
 * explicit null differently from a missing key on several models.
 */
export function pruneUndefined<T extends Record<string, unknown>>(value: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(value).filter(([, entry]) => entry !== undefined),
  ) as Partial<T>
}

/* -------------------------------------------- tour-scoped docs for tours ---- */

/**
 * Tour-scoped documents across a set of tours.
 *
 * ONE LISTENER PER TOUR, deliberately. The obvious implementation batches the
 * ids into `where('tourId', 'in', [...])`, and that is what this did first —
 * it fails.
 *
 * The rule for tour-scoped documents resolves `get(tours/$(tourId))` to decide
 * whether the reader is on that tour. Firestore caps how many document
 * accesses one request may make, and a query spanning twenty-six tours needs
 * twenty-six lookups, so the whole query is denied. Not trimmed — denied. The
 * page then renders zero shows and reports it as "0 cities" rather than as an
 * error, which is how a permissions failure turns into what looks like missing
 * data.
 *
 * A query constrained to a single tourId needs exactly one lookup, so it
 * always fits. More listeners, but each one is cheap and correct.
 *
 * Also note this can't be `useCollection('shows', [])`: an unconstrained read
 * of the collection is refused for the same reason.
 */
export function useDocsForTours<T>(collectionName: string, tourIds: string[]): QueryState<T> {
  const [state, setState] = useState<QueryState<T>>({ data: [], loading: true, error: null })

  // Sorted and joined so re-ordering the same ids doesn't tear down every
  // listener and re-bill the reads.
  const key = useMemo(() => [...tourIds].sort().join(','), [tourIds])

  useEffect(() => {
    const ids = key ? key.split(',') : []
    if (ids.length === 0) {
      setState({ data: [], loading: false, error: null })
      return
    }

    const buffer: Array<Array<WithId<T>>> = ids.map(() => [])
    let settled = 0

    const stops = ids.map((tourId, index) =>
      onSnapshot(
        query(collection(db, collectionName), where('tourId', '==', tourId)),
        (snapshot) => {
          buffer[index] = snapshot.docs.map(
            (entry) => repair({ id: entry.id, ...entry.data() }) as WithId<T>,
          )
          settled++
          setState({ data: buffer.flat(), loading: settled < ids.length, error: null })
        },
        (error) => {
          // One unreadable tour shouldn't blank the others, which is the whole
          // point of splitting them up.
          console.warn(`[${collectionName}] tour`, tourId, error)
          settled++
          setState((current) => ({ ...current, loading: settled < ids.length }))
        },
      ),
    )

    return () => stops.forEach((stop) => stop())
  }, [collectionName, key])

  return state
}

/** Shows across a set of tours. See `useDocsForTours`. */
export function useShowsForTours<T>(tourIds: string[]): QueryState<T> {
  return useDocsForTours<T>('shows', tourIds)
}
